import { useContext } from 'react'

import type { Route } from 'components/Structures/Breadcrumb'
import { navItems } from 'components/Structures/Header/constants'
import { useLocation } from 'react-router-dom'
import {
  type BreadcrumbContext,
  type BreadcrumbItem,
  DataBreadcrumb,
} from 'store/BreadcrumbContext/Breadcrumb'

export interface crumbs {
  crumbs: Route[]
  length: number
}

export const useBreadcrumb = (): void => {
  const location = useLocation()
  const breadcrumbContext = useContext(DataBreadcrumb)

  breadcrumbContext.currentPath = ''
  breadcrumbContext.labelName = ''
  breadcrumbContext.newLabel = ''

  const crumbs: Route[] = []

  const locationPath: string[] = location.pathname.split('/')

  locationPath.forEach((path, index) => {
    if (index === 0) {
      crumbs.push({
        label: newBreadcrumbContextOBJFactory().items.label,
        path: newBreadcrumbContextOBJFactory().items.path,
      })
      return
    }
    if (path === '') return

    const active = locationPath.length - 1 === index

    breadcrumbContext.currentPath += `/${path}`

    Object.keys(navItems).map(function (key, index) {
      if (navItems[key].item.length) {
        navItems[key].item.forEach((el) => {
          if (breadcrumbContext.currentPath === el.route) {
            breadcrumbContext.labelName = el.label
          }
        })
      }

      if (
        path.toLocaleLowerCase().includes(navItems[key]?.label.toLocaleLowerCase()) ||
        (navItems[key].route &&
          navItems[key]?.route.toLocaleLowerCase().includes(path.toLocaleLowerCase()))
      ) {
        breadcrumbContext.labelName = navItems[key].label
      }
    })

    breadcrumbContext.newLabel = breadcrumbContext.currentPath
      .toLocaleLowerCase()
      .includes(breadcrumbContext.labelName.toLocaleLowerCase())
      ? path
      : breadcrumbContext.labelName
    crumbs.push({
      active: active,
      label: breadcrumbContext.newLabel,
      path: breadcrumbContext.currentPath,
    })
  })

  managePath(breadcrumbContext.items, breadcrumbContext.newLabel, location.pathname)

  function managePath(breadcrumbItem: BreadcrumbItem, labelName: string, pathname: string) {
    if (breadcrumbItem.next) {
      managePath(breadcrumbItem.next, labelName, pathname)
      return
    }
    if (breadcrumbItem.label === labelName) return
    if (pathname === newBreadcrumbContextOBJFactory().items.path) {
      breadcrumbContext.indexToRemove = null
      breadcrumbContext.currentIndex = 0
      breadcrumbContext.items = newBreadcrumbContextOBJFactory().items
      return
    }

    if (breadcrumbContext.indexToRemove !== null) {
      breadcrumbContext.indexDuplicated = null
      const indexToRemove =
        breadcrumbContext.indexToRemove === -1 ? 3 : breadcrumbContext.indexToRemove

      removeFromIndex(newBreadcrumbContextOBJFactory(), breadcrumbContext.items, indexToRemove)
      return
    }

    const previousIndex = breadcrumbContext.currentIndex

    breadcrumbContext.currentIndex++

    breadcrumbItem.next = {
      index: breadcrumbContext.currentIndex,
      label: labelName,
      path: pathname,
      previous: {
        index: previousIndex,
        label: breadcrumbItem.label,
        path: breadcrumbItem.path,
      },
    }

    breadcrumbContext.indexDuplicated = null
    setLabelDuplicatedIndex(breadcrumbContext.items, labelName)

    if (breadcrumbContext.indexDuplicated) {
      removeByIndex(
        newBreadcrumbContextOBJFactory(),
        breadcrumbContext.items,
        breadcrumbContext.indexDuplicated,
        0,
      )
    }
  }

  function newBreadcrumbContextOBJFactory(): BreadcrumbContext {
    return {
      currentIndex: 0,
      currentPath: '',
      indexDuplicated: null,
      indexToRemove: null,
      items: {
        index: breadcrumbContext.items.index,
        label: breadcrumbContext.items.label,
        next: null,
        path: breadcrumbContext.items.path,
        previous: null,
      },
      labelName: '',
      newLabel: '',
    }
  }

  function removeFromIndex(
    newbreadcrumbContext: BreadcrumbContext,
    breadcrumbItem: BreadcrumbItem,
    indexToRemove: number,
    newbreadcrumbItem?: BreadcrumbItem,
  ) {
    if (breadcrumbItem.index === 0 && breadcrumbItem.next) {
      removeFromIndex(
        newbreadcrumbContext,
        breadcrumbItem.next,
        indexToRemove,
        newbreadcrumbContext.items,
      )
      return
    }

    if (breadcrumbItem.index <= indexToRemove) {
      if (newbreadcrumbItem) {
        newbreadcrumbItem.next = {
          index: breadcrumbItem.index,
          label: breadcrumbItem.label,
          path: breadcrumbItem.path,
          previous: {
            index: newbreadcrumbItem.index,
            label: newbreadcrumbItem.label,
            path: newbreadcrumbItem.path,
          },
        }
      }
      if (breadcrumbItem.next && newbreadcrumbItem?.next) {
        removeFromIndex(
          newbreadcrumbContext,
          breadcrumbItem.next,
          indexToRemove,
          newbreadcrumbItem?.next,
        )
        return
      }
    }

    if (newbreadcrumbContext) {
      breadcrumbContext.indexDuplicated = null
      breadcrumbContext.indexToRemove = null
      breadcrumbContext.currentIndex = newbreadcrumbContext.currentIndex
      breadcrumbContext.items = newbreadcrumbContext.items
    }
  }

  function removeByIndex(
    newbreadcrumbContext: BreadcrumbContext,
    breadcrumbItem: BreadcrumbItem,
    indexToRemove: number,
    currentIndex: number,
    newbreadcrumbItem?: BreadcrumbItem,
  ): void {
    newbreadcrumbContext.currentIndex = currentIndex
    if (breadcrumbItem.index === 0) {
      if (breadcrumbItem.next) {
        currentIndex++
        removeByIndex(
          newbreadcrumbContext,
          breadcrumbItem.next,
          indexToRemove,
          currentIndex,
          newbreadcrumbContext.items,
        )
      }
      return
    }

    if (breadcrumbItem.index === indexToRemove && breadcrumbItem.next) {
      removeByIndex(
        newbreadcrumbContext,
        breadcrumbItem.next,
        indexToRemove,
        currentIndex,
        newbreadcrumbItem,
      )
      return
    }

    if (newbreadcrumbItem) {
      newbreadcrumbItem.next = {
        index: currentIndex,
        label: breadcrumbItem.label,
        path: breadcrumbItem.path,
        previous: {
          index: newbreadcrumbItem.index,
          label: newbreadcrumbItem.label,
          path: newbreadcrumbItem.path,
        },
      }

      if (breadcrumbItem.next) {
        currentIndex++
        removeByIndex(
          newbreadcrumbContext,
          breadcrumbItem.next,
          indexToRemove,
          currentIndex,
          newbreadcrumbItem.next,
        )
        return
      }
    }

    if (newbreadcrumbContext) {
      breadcrumbContext.currentIndex = newbreadcrumbContext.currentIndex
      breadcrumbContext.indexToRemove = newbreadcrumbContext.indexToRemove
      breadcrumbContext.items = newbreadcrumbContext.items
    }
  }

  function setLabelDuplicatedIndex(breadcrumbItem: BreadcrumbItem, currentLabel: string): void {
    if (breadcrumbItem.label !== currentLabel && breadcrumbItem.next) {
      setLabelDuplicatedIndex(breadcrumbItem.next, currentLabel)
      return
    }

    if (breadcrumbItem.label === currentLabel && breadcrumbItem.next) {
      breadcrumbContext.indexDuplicated = breadcrumbItem.index
    }
  }
}

function handleCrumb(
  count: number,
  currentCrumbs: Route[],
  currentBreadcrumbContext: BreadcrumbItem,
): Route[] | undefined {
  if (currentBreadcrumbContext.label && currentBreadcrumbContext.path) {
    count++
    currentCrumbs.push({
      active: count > 1 && !currentBreadcrumbContext.next,
      label: currentBreadcrumbContext.label,
      path: currentBreadcrumbContext.path,
    })

    if (currentBreadcrumbContext.next) {
      handleCrumb(count, currentCrumbs, currentBreadcrumbContext.next)
    }

    return currentCrumbs
  }
}

export function mountBreadcrumb(breadcrumbItem: BreadcrumbItem): crumbs {
  const currentCrumbs: Route[] = []

  const crumbs = handleCrumb(0, currentCrumbs, breadcrumbItem)

  return { crumbs: crumbs || [], length: crumbs?.length || 0 }
}

export function addIndexToRemove(element: any, breadcrumbContext: BreadcrumbContext) {
  const indexToRemove = element.parentElement && element.parentElement.getAttribute('data-index')
  if (indexToRemove) {
    breadcrumbContext.indexToRemove = parseInt(indexToRemove)
    return
  }

  if (element.href) {
    breadcrumbContext.indexToRemove = -1
  }
}
